import React, { useState, useEffect } from 'react';
import { sectorsApi, usersApi } from '../api';
import { toast } from 'sonner';
import {
  Building2,
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  Loader2,
  CheckCircle,
  XCircle,
  User,
  Mail,
  Users,
  ChevronRight,
  ChevronDown,
  FolderTree,
  UserPlus,
  UserMinus,
} from 'lucide-react';

const Sectors = () => {
  const [sectors, setSectors] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [editingSector, setEditingSector] = useState(null);
  const [selectedSector, setSelectedSector] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedSectors, setExpandedSectors] = useState(new Set());
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    parent_id: '',
    manager_name: '',
    manager_email: '',
    is_active: true,
  });

  useEffect(() => {
    loadSectors();
    loadUsers();
  }, []);

  const loadSectors = async () => {
    try {
      setLoading(true);
      const response = await sectorsApi.list();
      setSectors(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar setores');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await usersApi.list({ is_active: true });
      setAllUsers(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const handleOpenModal = async (sector = null) => {
    if (sector) {
      try {
        const res = await sectorsApi.get(sector.id);
        const fullSector = res.data;
        setEditingSector(fullSector);
        setFormData({
          code: fullSector.code || '',
          name: fullSector.name || '',
          description: fullSector.description || '',
          parent_id: fullSector.parent_id || '',
          manager_name: fullSector.manager_name || '',
          manager_email: fullSector.manager_email || '',
          is_active: fullSector.is_active !== false,
        });
        setShowModal(true);
      } catch (error) {
        toast.error('Erro ao carregar dados do setor');
      }
    } else {
      setEditingSector(null);
      setFormData({
        code: '',
        name: '',
        description: '',
        parent_id: '',
        manager_name: '',
        manager_email: '',
        is_active: true,
      });
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSector(null);
  };

  const handleOpenUsersModal = async (sector) => {
    try {
      const response = await sectorsApi.get(sector.id);
      setSelectedSector(response.data);
      setShowUsersModal(true);
    } catch (error) {
      toast.error('Erro ao carregar dados do setor');
    }
  };

  const handleCloseUsersModal = () => {
    setShowUsersModal(false);
    setSelectedSector(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('O nome do setor é obrigatório');
      return;
    }
    
    setSaving(true);
    
    // Clean up empty strings to null for optional fields
    const cleanedData = {
      ...formData,
      code: formData.code || null,
      description: formData.description || null,
      parent_id: formData.parent_id || null,
      manager_name: formData.manager_name || null,
      manager_email: formData.manager_email || null,
    };
    
    try {
      if (editingSector) {
        await sectorsApi.update(editingSector.id, cleanedData);
        toast.success('Setor atualizado com sucesso!');
      } else {
        await sectorsApi.create(cleanedData);
        toast.success('Setor cadastrado com sucesso!');
      }
      
      handleCloseModal();
      loadSectors();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = Array.isArray(detail) 
        ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ')
        : (detail || 'Erro ao salvar setor');
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (sector) => {
    if (!window.confirm(`Desativar o setor "${sector.name}"?`)) return;
    
    try {
      await sectorsApi.delete(sector.id);
      toast.success('Setor desativado com sucesso!');
      loadSectors();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Erro ao desativar setor');
    }
  };

  const handleAssignUser = async (userId) => {
    if (!selectedSector) return;
    
    try {
      await sectorsApi.assignUser(selectedSector.id, userId);
      toast.success('Usuário vinculado ao setor!');
      // Reload sector data
      const response = await sectorsApi.get(selectedSector.id);
      setSelectedSector(response.data);
      loadSectors();
    } catch (error) {
      toast.error('Erro ao vincular usuário');
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!selectedSector) return;
    if (!window.confirm('Remover este usuário do setor?')) return;
    
    try {
      await sectorsApi.removeUser(selectedSector.id, userId);
      toast.success('Usuário removido do setor!');
      // Reload sector data
      const response = await sectorsApi.get(selectedSector.id);
      setSelectedSector(response.data);
      loadSectors();
    } catch (error) {
      toast.error('Erro ao remover usuário');
    }
  };

  const toggleExpanded = (sectorId) => {
    const newExpanded = new Set(expandedSectors);
    if (newExpanded.has(sectorId)) {
      newExpanded.delete(sectorId);
    } else {
      newExpanded.add(sectorId);
    }
    setExpandedSectors(newExpanded);
  };

  // Filter and organize sectors hierarchically
  const filteredSectors = sectors.filter(sector =>
    sector.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sector.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get root sectors (no parent) and their children
  const rootSectors = filteredSectors.filter(s => !s.parent_id);
  const getChildren = (parentId) => filteredSectors.filter(s => s.parent_id === parentId);

  // Available sectors for parent dropdown (excluding self and children)
  const getAvailableParents = () => {
    if (!editingSector) return sectors.filter(s => s.is_active);
    
    // Exclude self and check if it's a descendant
    const isDescendant = (sectorId, potentialAncestorId) => {
      const sector = sectors.find(s => s.id === sectorId);
      if (!sector) return false;
      if (sector.parent_id === potentialAncestorId) return true;
      if (sector.parent_id) return isDescendant(sector.parent_id, potentialAncestorId);
      return false;
    };
    
    return sectors.filter(s => 
      s.is_active && 
      s.id !== editingSector.id && 
      !isDescendant(s.id, editingSector.id)
    );
  };

  // Get users not assigned to any sector or available for this sector
  const getAvailableUsers = () => {
    if (!selectedSector) return [];
    const assignedUserIds = selectedSector.users?.map(u => u.id) || [];
    return allUsers.filter(u => 
      u.is_active && 
      !assignedUserIds.includes(u.id) &&
      (!u.sector_id || u.sector_id === selectedSector.id)
    );
  };

  const renderSectorRow = (sector, level = 0) => {
    const children = getChildren(sector.id);
    const hasChildren = children.length > 0;
    const isExpanded = expandedSectors.has(sector.id);
    
    return (
      <React.Fragment key={sector.id}>
        <tr className="hover:bg-gray-800/50">
          <td className="px-4 py-3">
            <div className="flex items-center" style={{ paddingLeft: `${level * 24}px` }}>
              {hasChildren ? (
                <button
                  onClick={() => toggleExpanded(sector.id)}
                  className="p-1 mr-2 text-gray-400 hover:text-white"
                >
                  {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
              ) : (
                <span className="w-7" />
              )}
              <span className="font-mono text-white">{sector.code}</span>
            </div>
          </td>
          <td className="px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="text-white">{sector.name}</span>
              {sector.parent_name && (
                <span className="text-xs text-gray-500 bg-gray-700 px-2 py-0.5 rounded">
                  → {sector.parent_name}
                </span>
              )}
            </div>
          </td>
          <td className="px-4 py-3 text-gray-400">{sector.manager_name || '-'}</td>
          <td className="px-4 py-3">
            <button
              onClick={() => handleOpenUsersModal(sector)}
              className="flex items-center gap-1 text-blue-400 hover:text-blue-300"
            >
              <Users size={14} />
              <span>{sector.users_count || 0}</span>
            </button>
          </td>
          <td className="px-4 py-3">
            {sector.is_active ? (
              <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400 flex items-center gap-1 w-fit">
                <CheckCircle size={12} />
                Ativo
              </span>
            ) : (
              <span className="px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400 flex items-center gap-1 w-fit">
                <XCircle size={12} />
                Inativo
              </span>
            )}
          </td>
          <td className="px-4 py-3">
            <div className="flex items-center justify-end gap-1">
              <button
                onClick={() => handleOpenModal(sector)}
                className="p-2 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded-lg"
                title="Editar"
              >
                <Edit size={18} />
              </button>
              {sector.is_active && (
                <button
                  onClick={() => handleDelete(sector)}
                  className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg"
                  title="Desativar"
                >
                  <Trash2 size={18} />
                </button>
              )}
            </div>
          </td>
        </tr>
        {hasChildren && isExpanded && children.map(child => renderSectorRow(child, level + 1))}
      </React.Fragment>
    );
  };

  return (
    <div className="space-y-6" data-testid="sectors-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="text-green-400" />
            Cadastro de Setores
          </h1>
          <p className="text-gray-400 mt-1">Gerencie os setores/departamentos da organização com hierarquia</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="btn-primary"
          data-testid="add-sector-btn"
        >
          <Plus size={18} />
          Novo Setor
        </button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
          <input
            type="text"
            placeholder="Buscar por nome ou código..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input w-full pl-10"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Building2 className="text-blue-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Total de Setores</p>
              <p className="text-2xl font-bold text-blue-400">{sectors.length}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <CheckCircle className="text-green-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Ativos</p>
              <p className="text-2xl font-bold text-green-400">
                {sectors.filter(s => s.is_active).length}
              </p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <FolderTree className="text-purple-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Raiz</p>
              <p className="text-2xl font-bold text-purple-400">
                {sectors.filter(s => !s.parent_id && s.is_active).length}
              </p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-500/20 rounded-lg">
              <Users className="text-yellow-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Usuários Vinculados</p>
              <p className="text-2xl font-bold text-yellow-400">
                {sectors.reduce((sum, s) => sum + (s.users_count || 0), 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-green-500" size={32} />
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Código</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Nome</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Responsável</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Usuários</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredSectors.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    Nenhum setor encontrado
                  </td>
                </tr>
              ) : (
                rootSectors.map(sector => renderSectorRow(sector))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Sector Form Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h2 className="text-lg font-semibold text-white">
                {editingSector ? 'Editar Setor' : 'Novo Setor'}
              </h2>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Código</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="SET-001 (auto)"
                    className="input w-full"
                    data-testid="sector-code-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Nome do Setor <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Qualidade"
                    className="input w-full"
                    required
                    data-testid="sector-name-input"
                  />
                </div>
              </div>

              {/* Parent Sector - Hierarchy */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <FolderTree size={14} className="inline mr-1" />
                  Setor Pai (Hierarquia)
                </label>
                <select
                  value={formData.parent_id}
                  onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
                  className="input w-full"
                  data-testid="sector-parent-select"
                >
                  <option value="">Nenhum (Setor Raiz)</option>
                  {getAvailableParents().map(sector => (
                    <option key={sector.id} value={sector.id}>
                      {sector.code} - {sector.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Selecione um setor pai para criar uma hierarquia organizacional
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Descrição</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrição do setor..."
                  className="input w-full"
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <User size={14} className="inline mr-1" />
                    Responsável
                  </label>
                  <input
                    type="text"
                    value={formData.manager_name}
                    onChange={(e) => setFormData({ ...formData, manager_name: e.target.value })}
                    placeholder="Nome do responsável"
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Mail size={14} className="inline mr-1" />
                    Email do Responsável
                  </label>
                  <input
                    type="email"
                    value={formData.manager_email}
                    onChange={(e) => setFormData({ ...formData, manager_email: e.target.value })}
                    placeholder="email@empresa.com"
                    className="input w-full"
                  />
                </div>
              </div>
              
              <div className="flex items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-green-500"
                  />
                  <span className="text-sm font-medium text-gray-300">Setor Ativo</span>
                </label>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button type="button" onClick={handleCloseModal} className="btn-secondary" disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
                  {saving ? <><Loader2 size={18} className="animate-spin" /> Salvando...</> : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Users Management Modal */}
      {showUsersModal && selectedSector && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <div>
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Users className="text-blue-400" />
                  Usuários do Setor
                </h2>
                <p className="text-sm text-gray-400">{selectedSector.code} - {selectedSector.name}</p>
              </div>
              <button onClick={handleCloseUsersModal} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Current Users */}
              <div>
                <h3 className="text-sm font-medium text-gray-300 mb-3">
                  Usuários Vinculados ({selectedSector.users?.length || 0})
                </h3>
                {selectedSector.users?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedSector.users.map(user => (
                      <div key={user.id} className="flex items-center justify-between bg-gray-700/50 p-3 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                            <User className="text-green-400" size={20} />
                          </div>
                          <div>
                            <p className="text-white font-medium">{user.full_name}</p>
                            <p className="text-sm text-gray-400">{user.email}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveUser(user.id)}
                          className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"
                          title="Remover do setor"
                        >
                          <UserMinus size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">Nenhum usuário vinculado a este setor</p>
                )}
              </div>

              {/* Add Users */}
              <div className="border-t border-gray-700 pt-4">
                <h3 className="text-sm font-medium text-gray-300 mb-3">
                  <UserPlus size={14} className="inline mr-1" />
                  Adicionar Usuário
                </h3>
                {getAvailableUsers().length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {getAvailableUsers().map(user => (
                      <div key={user.id} className="flex items-center justify-between bg-gray-900/50 p-3 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center">
                            <User className="text-gray-400" size={20} />
                          </div>
                          <div>
                            <p className="text-white">{user.full_name}</p>
                            <p className="text-sm text-gray-400">{user.email}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleAssignUser(user.id)}
                          className="p-2 text-green-400 hover:bg-green-500/20 rounded-lg"
                          title="Vincular ao setor"
                        >
                          <UserPlus size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">Todos os usuários já estão vinculados a setores</p>
                )}
              </div>
            </div>
            
            <div className="flex justify-end p-4 border-t border-gray-700">
              <button onClick={handleCloseUsersModal} className="btn-secondary">
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sectors;
